import { Navigate, createBrowserRouter } from "react-router-dom";
import React, { Suspense, lazy } from "react";
import { getRouteConfig } from "@/router/route.utils";
import Layout from "@/components/organisms/Layout";
import Root from "@/layouts/Root";

// Lazy load components
const AdCampaigns = lazy(() => import("@/components/pages/AdCampaigns"));
const Campaigns = lazy(() => import("@/components/pages/Campaigns"));
const Clients = lazy(() => import("@/components/pages/Clients"));
const AdBuilder = lazy(() => import("@/components/pages/AdBuilder"));
const FacebookLogin = lazy(() => import("@/components/pages/FacebookLogin"));
const FacebookCallback = lazy(() => import("@/components/pages/FacebookCallback"));
const NotFound = lazy(() => import("@/components/pages/NotFound"));
const Login = lazy(() => import("@/components/pages/Login"));
const Signup = lazy(() => import("@/components/pages/Signup"));
const Callback = lazy(() => import("@/components/pages/Callback"));
const ErrorPage = lazy(() => import("@/components/pages/ErrorPage"));
const ResetPassword = lazy(() => import("@/components/pages/ResetPassword"));
const PromptPassword = lazy(() => import("@/components/pages/PromptPassword"));
const Templates = lazy(() => import("@/components/pages/Templates"));
// Create route helper
const createRoute = ({
  path,
  index,
  element,
  access,
  children,
  ...meta
}) => {
  // Get config for this route
  let configPath;
  if (index) {
    configPath = "/";
  } else {
    configPath = typeof path === 'string' && path.startsWith('/') ? path : `/${String(path)}`;
  }

  const config = getRouteConfig(configPath);
  const finalAccess = access || config?.allow;

  const route = {
    ...(index ? { index: true } : { path: String(path) }),
    element: element ? <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
    <div className="text-center space-y-4">
      <svg className="animate-spin h-12 w-12 text-blue-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
      </svg>
    </div>
  </div>}>{element}</Suspense> : element,
    handle: {
      access: finalAccess,
      ...meta,
    },
  };

  if (children && Array.isArray(children) && children.length > 0) {
    route.children = children;
  }

  return route;
};

// Facebook protected route component
function FacebookProtectedRoute({ children }) {
  // Keep existing Facebook authentication logic
  return <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
    <div className="text-center space-y-4">
      <svg className="animate-spin h-12 w-12 text-blue-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 718-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
      </svg>
    </div>
  </div>}>{children}</Suspense>;
}

// Route definitions
export const router = createBrowserRouter([
  {
    path: "/",
    element: <Root />,
    children: [
createRoute({
        path: "login",
        element: <Login />
      }),
      createRoute({
        path: "signup", 
        element: <Signup />
      }),
      createRoute({
        path: "callback",
        element: <Callback />
      }),
      createRoute({
        path: "error",
        element: <ErrorPage />
      }),
      createRoute({
        path: "reset-password/:appId/:fields",
        element: <ResetPassword />
      }),
      createRoute({
        path: "prompt-password/:appId/:emailAddress/:provider",
        element: <PromptPassword />
      }),
      {
        path: "/",
        element: <Layout />,
        children: [
          createRoute({
            index: true,
            element: <FacebookLogin />
          }),
createRoute({
            path: "/campaigns",
            element: <AdCampaigns />
          }),
          createRoute({
            path: "campaigns/:id",
            element: <Campaigns />
          }),
          createRoute({
            path: "adsets", 
            element: <Navigate to="/campaigns?tab=adsets" replace />
          }),
          createRoute({
            path: "ads",
            element: <Navigate to="/campaigns?tab=ads" replace />
          }),
          createRoute({
            path: "builder",
element: <AdBuilder />
          }),
          createRoute({
            path: "/builder",
            element: <AdBuilder />
          }),
          createRoute({
            path: "clients",
            element: <Clients />
          }),
          createRoute({
            path: "facebook-login",
            element: <FacebookLogin />
          }),
          createRoute({
            path: "facebook/callback", 
            element: <FacebookCallback />
          }),
          createRoute({
            path: "facebook/login",
            element: <FacebookLogin />
          }),
createRoute({
            path: "*",
            element: <NotFound />
          }),
          createRoute({
            path: "templates",
            element: <Templates />
          })
        ]
      }
    ]
  }
], {
  future: {
    v7_skipActionErrorRevalidation: true,
    v7_normalizeFormMethod: true
  }
});